import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef
} from 'react';
import * as utils from '../components/utils.js';
import * as menu from "../assets/menus/menu.js";
import { useTranslation } from 'react-i18next';

const Choices = forwardRef(({
    voc,
    selected,
    multiple,
    value = "Id",
    text = "Name",
    textDelegate,
    onChange,
    dropDownParent,
    allowClear = true,
    placeholder ,
    maxItemLength = 100,
    remoteSource,
    allKeywords,
    minText = 3,
    allowTagging = false,
    tagInputInDropdown = false,
    className = "",
    disabled = false
}, ref) => {


    const { t } = useTranslation();

    //#region label
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', {
        placeholder: '<-- Seleziona un elemento -->',
    }, true, true);

    i18n.addResourceBundle('en', 'labels', {
        placeholder: '<-- Select an element -->',
    }, true, true);


    const [data, setData] = useState({ voc, selected });
    const [select2, setSelect2] = useState(null);

    useEffect(() => {
        data.voc = voc;
        setData(utils.clone(data));
    }, [voc]);

    // Guard to avoid reacting to programmatic sync as if it was a user action
    const isProgrammaticChange = useRef(false);

    // Keep internal selection in sync when parent updates `selected` prop
    useEffect(() => {
        const nextSelected = selected || [];
        const currentSelected = data.selected || [];
        const nextKeys = nextSelected.map(s => String(s[value]));
        const currentKeys = currentSelected.map(s => String(s[value]));

        const arraysDiffer =
            nextKeys.length !== currentKeys.length ||
            nextKeys.some((k, i) => k !== currentKeys[i]);

        if (arraysDiffer) {
            data.selected = nextSelected;
            setData(utils.clone(data));
        }

        // Always sync Select2 DOM value when select2 is available — handles the case
        // where select2 was re-initialized (e.g. after voc loaded) but arraysDiffer=false
        if (select2) {
            const $r = window.$(select2);
            const rawVal = $r.val();
            const currentVals = (Array.isArray(rawVal)
                ? rawVal
                : (rawVal !== null && rawVal !== undefined ? [rawVal] : [])).map(v => String(v));
            const needSync =
                nextKeys.length !== currentVals.length ||
                nextKeys.some((k, i) => k !== currentVals[i]);
            if (needSync) {
                isProgrammaticChange.current = true;
                $r.val(nextKeys).trigger('change.select2');
                setTimeout(() => { isProgrammaticChange.current = false; }, 0);
            }
        }
    }, [selected, select2, value]);

    useEffect(() => {
        (async () => { })();
    }, [data]);

    // Handle disabled state changes
    useEffect(() => {
        if (select2) {
            const $r = window.$(select2);
            $r.prop('disabled', disabled);

            // Update cursor style based on disabled state
            const $container = $r.next('.select2');
            if (disabled) {
                $container.css('cursor', 'not-allowed');
                $container.find('.select2-selection').css('cursor', 'not-allowed');
                $container.find('.select2-selection__rendered').css('cursor', 'not-allowed');
            } else {
                $container.css('cursor', 'pointer');
                $container.find('.select2-selection').css('cursor', 'pointer');
                $container.find('.select2-selection__rendered').css('cursor', 'pointer');
            }
        }
    }, [disabled, select2]);

    useImperativeHandle(ref, () => ({
        clearSelection() {
            if (select2) {
                window.$(select2).val(null).trigger('change');
                data.selected = [];
                setData(utils.clone(data));
            }
        }
    }));

    const _getText = (item, list) => {
        let _text = textDelegate ? textDelegate(item) : item[text];

        if (!list && maxItemLength > 0 && _text && _text.length > maxItemLength) {
            _text = `${_text.substring(0, maxItemLength)}...`;
        }

        return _text;
    };

    const _getElements = () => {
        const output = [];
        if (allowClear && !multiple) {
            output.push(<option key="__placeholder__" value="">{placeholder || t('placeholder', { ns: 'labels' })}</option>);
        }
        output.push(...data.voc.map((gv) =>
        (<option
            key={gv[value]}
            value={gv[value]}>
            {_getText(gv)}
        </option>)
        ));
        return output;
    };

    return (
        <>
            <select
                ref={(r) => {
                    setSelect2(r);
                    const $r = window.$(r);

                    if ($r.hasClass("select2-hidden-accessible")) {
                        $r.select2('destroy');
                    }

                    // Aggiungi stile globale per Select2 se richiesto sfondo bianco
                    if (className.includes('bg-white')) {
                        const styleId = 'select2-white-style';
                        // Rimuovi lo stile esistente se presente
                        if (window.$('#' + styleId).length) {
                            window.$('#' + styleId).remove();
                        }
                        
                        // Aggiungi un nuovo stile con selettori specifici e !important
                        window.$('<style id="' + styleId + '">')
                            .prop('type', 'text/css')
                            .html(`
                                .select2-container--default .select2-dropdown {
                                    background-color: white !important;
                                }
                                .select2-container--default .select2-results__option {
                                    background-color: white !important;
                                    color: black !important;
                                }
                                .select2-container--default .select2-results__option--highlighted[aria-selected] {
                                    background-color: #f8f9fa !important;
                                    color: black !important;
                                }
                                .select2-container--default .select2-results__message {
                                    background-color: white !important;
                                    color: #6c757d !important;
                                }
                                .select2-container--default .select2-selection--single {
                                    background-color: white !important;
                                }
                                .select2-container--default .select2-selection--multiple {
                                    background-color: white !important;
                                }
                            `)
                            .appendTo('head');
                    }

                    const initOptions = {
                        dropdownParent: dropDownParent ? window.$(`#${dropDownParent}`) : null,
                        escapeMarkup: function (markup) {
                            return markup; // Allow HTML in textDelegate
                        },
                        templateResult: function (data) {
                            if (!data || !data.text) return data.text || '';
                            // Check if text contains HTML (has <br> or <small> tags)
                            if (data.text.includes('<') && data.text.includes('>')) {
                                return window.$('<span>').html(data.text);
                            }
                            return data.text;
                        },
                        templateSelection: function (data) {
                            // For selection, show only the first line (before <br> if present)
                            const text = data.text || '';
                            const firstLine = text.split('<br>')[0].replace(/<[^>]*>/g, '');
                            return window.$('<span>', {
                                class: 'text-truncate d-block w-100',
                                title: firstLine,
                                text: firstLine
                            });
                        },
                        // Enable tagging (typing new values) when requested
                        tags: allowTagging ? true : false,
                        tokenSeparators: allowTagging ? [',', ';'] : [],
                        minimumResultsForSearch: (allowTagging || tagInputInDropdown) ? 0 : undefined,
                        createTag: allowTagging ? function (params) {
                            const term = (params.term || '').trim();
                            if (!term) return null;
                            // Avoid duplicate tags by text (case-insensitive)
                            const exists = $r.find('option').toArray().some(o => (window.$(o).text() || '').toLowerCase() === term.toLowerCase());
                            if (exists) return null;
                            return { id: term, text: term, newTag: true };
                        } : undefined,
                        ajax: remoteSource ? {
                            transport: async (params, success, failure) => {
                                if ((params?.data?.term?.length ?? 0) > minText) {
                                    const _result = await remoteSource({ text: params?.data?.term });
                                    const _items = _result.map((i) => ({
                                        id: i[value],
                                        text: _getText(i),
                                        data: i
                                    }));
                                    success({ results: _items });
                                } else {
                                    return [];
                                }
                            }
                        } : (allowTagging && Array.isArray(allKeywords) && allKeywords.length > 0 ? {
                            transport: (params, success) => {
                                const termRaw = params?.data?.term || '';
                                const term = termRaw.trim().toLowerCase();
                                if ((termRaw?.length ?? 0) <= minText) {
                                    return success({ results: [] });
                                }

                                const optionTexts = new Set($r.find('option').toArray().map(o => (window.$(o).text() || '').toLowerCase()));

                                const mapItem = (item) => {
                                    if (typeof item === 'string') {
                                        return { id: item, text: item, data: { [value]: item, [text]: item } };
                                    }
                                    const idVal = item?.[value] ?? item?.Id ?? item?.id ?? item?.Keyword ?? item?.keyword;
                                    const textVal = (textDelegate ? textDelegate(item) : (item?.[text] ?? item?.Name ?? item?.label ?? String(idVal)));
                                    return { id: idVal, text: textVal, data: item };
                                };

                                const uniqueById = (arr) => {
                                    const seen = new Set();
                                    const out = [];
                                    for (const it of arr) {
                                        if (it?.id == null) continue;
                                        const key = String(it.id).toLowerCase();
                                        if (!seen.has(key)) { seen.add(key); out.push(it); }
                                    }
                                    return out;
                                };

                                const results = uniqueById(
                                    allKeywords
                                        .map(mapItem)
                                        .filter(k => (k.text || '').toLowerCase().includes(term))
                                        .filter(k => !optionTexts.has((k.text || '').toLowerCase()))
                                ).slice(0, 50);

                                success({ results });
                            }
                        } : null)
                    };

                    // When requested, move typing to the dropdown by enabling dropdown search adapter
                    if (allowTagging && tagInputInDropdown && window.$?.fn?.select2?.amd?.require) {
                        try {
                            const S2Utils = window.$.fn.select2.amd.require('select2/utils');
                            const Dropdown = window.$.fn.select2.amd.require('select2/dropdown');
                            const DropdownSearch = window.$.fn.select2.amd.require('select2/dropdown/search');
                            const AttachBody = window.$.fn.select2.amd.require('select2/dropdown/attachBody');
                            const CustomDropdown = S2Utils.Decorate(S2Utils.Decorate(Dropdown, DropdownSearch), AttachBody);
                            initOptions.dropdownAdapter = CustomDropdown;
                        } catch (err) {
                            // Fallback silently if AMD modules are not available
                        }
                    }

                    $r.select2(initOptions);

                    // Apply disabled state if requested
                    if (disabled) {
                        $r.prop('disabled', true);
                        $r.select2('destroy');
                        $r.select2(initOptions);
                    }
                    // Ensure the Select2 dropdown is rendered above modals/backdrops (z-index issues can
                    // make the arrow flip without showing any options).
                    const ensureDropdownOnTop = () => {
                        const zIndex = 20000;
                        window.$('.select2-container--open').css('z-index', zIndex);
                        window.$('.select2-dropdown').css('z-index', zIndex);
                    };
                    $r.off('select2:open.aiPlanZ');
                    $r.on('select2:open.aiPlanZ', ensureDropdownOnTop);
                    ensureDropdownOnTop();

                    // Force pointer cursor on select2 rendered elements
                    const applyPointerCursor = () => {
                        const $container = $r.next('.select2');
                        if (!disabled) {
                            $container.css('cursor', 'pointer');
                            $container.find('.select2-selection').css('cursor', 'pointer');
                            $container.find('.select2-selection__rendered').css('cursor', 'pointer');
                            window.$('.select2-dropdown').css('cursor', 'pointer');
                            window.$('.select2-results__option').css('cursor', 'pointer');
                            window.$('.select2-search__field').css('cursor', 'pointer');
                        } else {
                            $container.css('cursor', 'not-allowed');
                            $container.find('.select2-selection').css('cursor', 'not-allowed');
                            $container.find('.select2-selection__rendered').css('cursor', 'not-allowed');
                        }
                    };
                    applyPointerCursor();
                    
                    // Aggiungi listener per quando il dropdown viene aperto
                    if (className.includes('bg-white')) {
                        $r.on('select2:open', function() {
                            window.$('.select2-dropdown').css('background-color', 'white');
                            window.$('.select2-results__option').css('background-color', 'white');
                            window.$('.select2-results__message').css('background-color', 'white');
                            // Re-apply cursor when dropdown mounts
                            window.$('.select2-dropdown').css('cursor', 'pointer');
                            window.$('.select2-results__option').css('cursor', 'pointer');
                            window.$('.select2-search__field').css('cursor', 'pointer');
                        });
                    }

                    // If we push typing into the dropdown, hide the inline search and focus dropdown search
                    if (allowTagging && tagInputInDropdown) {
                        const $container = $r.next('.select2');
                        $container.find('.select2-search--inline').css('display', 'none');

                        $r.on('select2:open', function() {
                            const $dropdown = window.$('.select2-container--open .select2-dropdown');
                            const $searchWrap = $dropdown.find('.select2-search--dropdown');
                            const $searchInput = $searchWrap.find('.select2-search__field');

                            if ($searchInput.length) {
                                // Set requested placeholder
                                $searchInput.attr('placeholder', t('keywordsPlaceholder', { ns: 'labels' }));
                                $searchInput.trigger('focus');
                            }
                        });
                    }

                    // Applica stile personalizzato se richiesto sfondo bianco
                    if (className.includes('bg-white')) {
                        // Applica uno stile personalizzato per il dropdown
                        window.$('.select2-dropdown').css('background-color', 'white');
                        window.$('.select2-results__option').css('background-color', 'white');
                        window.$('.select2-results__message').css('background-color', 'white');
                        // And ensure pointer cursor
                        window.$('.select2-dropdown').css('cursor', 'pointer');
                        window.$('.select2-results__option').css('cursor', 'pointer');
                        window.$('.select2-search__field').css('cursor', 'pointer');
                    }

                    $r.off("change");
                    $r.on("change", (e) => {
                        if (isProgrammaticChange.current) {
                            return;
                        }
                        const _data = $r.select2('data');
                        if (_data.filter(x => x.data).length > 0) {
                            const _newSelection = _data.filter(x => x.data).map(x => x.data);
                            const _actualSelection = data.selected ?
                                data.selected.filter(x => _data.find(y => y.id == x[value] && !y.data)) : [];
                            data.selected = _actualSelection.concat(_newSelection);
                            setData(JSON.parse(JSON.stringify(data)));
                        } else {
                            data.selected = [];
                            const _selected = $r.find(`:selected`).toArray();
                            for (var s of _selected) {
                                const $s = window.$(s);
                                if ($s.attr("value")) {
                                    const optionValue = $s.attr("value");
                                    const optionText = $s.text();
                                    const itemFromVoc = data.voc.find(x => String(x[value]) == String(optionValue));
                                    data.selected.push(itemFromVoc ? itemFromVoc : { [value]: optionValue, [text]: optionText });
                                }
                            }
                            setData(JSON.parse(JSON.stringify(data)));
                        }

                        if (onChange) onChange(data);
                    });
                }}
                className={`multi-select select2-groups inputStyle ${className.includes('bg-white') ? '' : 'orange-select'} ${className}`.trim()}
                multiple={multiple ? true : null}
                defaultValue={multiple
                    ? ((data.selected || []).map(s => String(s[value])))
                    : ((data.selected && data.selected.length > 0) ? String(data.selected[0][value]) : "")}
            >
                {_getElements()}
            </select>
        </>
    );
});

export default Choices;
